import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSectionStore, Section } from '../../store/section-store';
import { cloudfrontUrl } from '../../constants/imageUrl';
import { PRIMARY_COLOR } from '../../constants/color';
import NiceTimeDisplay from './NiceTimeDisplay';

const menuItems = [
  { href: '/', label: 'Chez nous' },
  { href: '/products', label: 'Nos créations' },
  { href: '/about', label: 'Notre univers' },
];

const HamburgerIcon = ({
  isOpen = false,
  isWhite = false,
  isBlack = false,
}: {
  isOpen?: boolean;
  isWhite?: boolean;
  isBlack?: boolean;
}) => (
  <div className="w-6 h-6 flex flex-col justify-center space-y-1">
    <span
      className={`block h-0.5 w-6 ${
        isWhite ? 'bg-white' : isBlack ? 'bg-black' : 'bg-gray-600'
      } transition-all duration-700 ease-in-out ${
        isOpen ? 'rotate-45 translate-y-1.5' : ''
      }`}
    />
    <span
      className={`block h-0.5 w-6 ${
        isWhite ? 'bg-white' : isBlack ? 'bg-black' : 'bg-gray-600'
      } transition-all duration-700 ease-in-out ${isOpen ? 'opacity-0' : ''}`}
    />
    <span
      className={`block h-0.5 w-6 ${
        isWhite ? 'bg-white' : isBlack ? 'bg-black' : 'bg-gray-600'
      } transition-all duration-700 ease-in-out ${
        isOpen ? '-rotate-45 -translate-y-1.5' : ''
      }`}
    />
  </div>
);

const CloseIcon = () => (
  <div className="w-6 h-6 flex items-center justify-center">
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </div>
);

const MenuItems = ({ onNavigate }: { onNavigate?: () => void }) => {
  const location = useLocation();

  return (
    <ul className="space-y-4">
      {menuItems.map((item, index) => {
        // Check if current path matches exactly or starts with the item href (for nested routes)
        const isActive =
          item.href === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.href);

        return (
          <li key={index}>
            <Link
              to={item.href}
              className={`block text-gray-700 hover:text-gray-900 transition-colors py-2 ${
                isActive ? 'font-bold' : ''
              }`}
              onClick={onNavigate}
            >
              {item.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

export default function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const activeSection = useSectionStore((state) => state.activeSection);
  const navigate = useNavigate();
  const isShowPopup = import.meta.env.VITE_IS_POPUP_IMAGE_VISIBLE === 'true';

  // Check if popup should be shown on component mount
  useEffect(() => {
    const popupImageUrl = import.meta.env.VITE_POPUP_IMAGE_URL;
    if (popupImageUrl) {
      const hasSeenPopup = localStorage.getItem(`popup_seen_${popupImageUrl}`);
      if (!hasSeenPopup) {
        setShowPopup(true);
      }
    }
  }, []);

  // Determine the effective section based on current route
  const getEffectiveSection = () => {
    // On home page, use the actual active section from scroll detection
    if (location.pathname === '/') {
      return activeSection;
    }

    if (location.pathname === '/about') {
      return Section.Footer;
    }
    // On all other pages, fix to Menu section
    return Section.Menu;
  };

  // Get logo based on effective section
  const getCurrentLogo = () => {
    const effectiveSection = getEffectiveSection();
    switch (effectiveSection) {
      case Section.Header:
        return `${cloudfrontUrl}/logo_red.png`;
      case Section.Menu:
        return `${cloudfrontUrl}/logo_black.png`;
      case Section.Footer:
        return `${cloudfrontUrl}/logo_white.png`;
      default:
        return `${cloudfrontUrl}/logo_red.png`;
    }
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const toggleDesktopMenu = () => setIsDesktopMenuOpen(!isDesktopMenuOpen);

  const closeAllMenus = () => {
    setIsMobileMenuOpen(false);
    setIsDesktopMenuOpen(false);
  };

  const closePopup = () => {
    const popupImageUrl = import.meta.env.VITE_POPUP_IMAGE_URL;
    if (popupImageUrl) {
      localStorage.setItem(`popup_seen_${popupImageUrl}`, 'true');
    }
    setShowPopup(false);
  };

  return (
    <>
      {/* Mobile Navigation - Top Bar */}
      <nav className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 overflow-hidden w-full">
        {/* Base background layer */}
        <div className="absolute inset-0 bg-white" />

        {/* Pattern background layer */}
        <div
          className={`absolute inset-0 transition-opacity duration-4000 ease-in-out ${
            getEffectiveSection() === Section.Menu ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url(${cloudfrontUrl}/pattern_x.png)`,
            backgroundSize: '120% 110%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Color background layer */}
        <div
          className={`absolute inset-0 transition-opacity duration-4000 ease-in-out ${
            getEffectiveSection() === Section.Footer
              ? 'opacity-100'
              : 'opacity-0'
          }`}
          style={{ backgroundColor: PRIMARY_COLOR }}
        />

        {/* Content layer */}
        <div className="relative flex items-center justify-between h-full px-4 z-10">
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md transition-all duration-200 hover:scale-110"
            aria-label="Toggle menu"
          >
            <HamburgerIcon
              isOpen={isMobileMenuOpen}
              isWhite={getEffectiveSection() === Section.Footer}
              isBlack={getEffectiveSection() === Section.Menu}
            />
          </button>
          <div className="flex-1" />
          <img
            src={getCurrentLogo()}
            alt="KSM Logo"
            className="h-12 w-auto transition-all duration-4000 ease-in-out"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
          />
        </div>
      </nav>

      {/* Desktop Navigation - Always visible collapsed sidebar */}
      <nav className="hidden lg:flex fixed left-0 top-0 h-screen z-40 w-16 overflow-hidden">
        {/* Base background layer */}
        <div className="absolute inset-0 bg-white" />

        {/* Pattern background layer */}
        <div
          className={`absolute inset-0 transition-opacity duration-4000 ease-in-out ${
            getEffectiveSection() === Section.Menu ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url(${cloudfrontUrl}/pattern.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Color background layer */}
        <div
          className={`absolute inset-0 transition-opacity duration-4000 ease-in-out ${
            getEffectiveSection() === Section.Footer
              ? 'opacity-100'
              : 'opacity-0'
          }`}
          style={{ backgroundColor: PRIMARY_COLOR }}
        />

        {/* Content layer */}
        <div className="relative flex flex-col w-full items-center py-6 z-10">
          {/* Nice Time Display */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <NiceTimeDisplay currentSection={getEffectiveSection()} />
          </div>

          <button
            onClick={toggleDesktopMenu}
            className="p-2 rounded-md transition-all duration-200 hover:scale-110 mb-8 relative z-30"
            aria-label="Toggle menu"
          >
            <HamburgerIcon
              isWhite={getEffectiveSection() === Section.Footer}
              isBlack={getEffectiveSection() === Section.Menu}
            />
          </button>

          {/* Logo icon at bottom of desktop navigation */}
          <div
            className="mt-auto mb-6 cursor-pointer"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
          >
            <img
              src={getCurrentLogo()}
              alt="KSM Logo"
              className="h-12 hover:h-12 w-auto transition-all duration-4000 ease-in-out"
            />
          </div>
        </div>
      </nav>

      {/* Desktop Navigation - Expanded Sidebar */}
      <nav
        className={`hidden lg:flex fixed left-16 top-0 h-screen bg-white z-50 transition-all duration-500 ease-in-out shadow-lg ${
          isDesktopMenuOpen ? 'w-64' : 'w-0'
        }`}
      >
        <div
          className={`flex flex-col w-full overflow-hidden ${
            isDesktopMenuOpen
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-4'
          } transition-all duration-500 ease-in-out delay-100`}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 min-w-64">
            <div
              className="text-xl font-semibold text-gray-900 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
            >
              <img
                src={`${cloudfrontUrl}/logo_ksm_straight_nice.png`}
                alt="KSM Logo"
              />
            </div>
            <button
              onClick={toggleDesktopMenu}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 p-6 min-w-64">
            <MenuItems onNavigate={closeAllMenus} />
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-500 ease-in-out ${
          isMobileMenuOpen
            ? 'bg-black bg-opacity-50 pointer-events-auto'
            : 'bg-black bg-opacity-0 pointer-events-none'
        }`}
        onClick={toggleMobileMenu}
      >
        <div
          className={`fixed left-0 top-16 bottom-0 w-64 bg-white shadow-lg transition-all duration-500 ease-in-out ${
            isMobileMenuOpen
              ? 'translate-x-0 opacity-100'
              : '-translate-x-full opacity-0'
          }`}
        >
          <div
            className={`p-6 transition-all duration-500 ease-in-out delay-100 ${
              isMobileMenuOpen
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <MenuItems onNavigate={closeAllMenus} />
          </div>
        </div>
      </div>

      {/* Popup Modal */}
      {showPopup && import.meta.env.VITE_POPUP_IMAGE_URL && isShowPopup && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={closePopup}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh] w-auto h-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closePopup}
              className="absolute -top-2 -right-2 z-10 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
              aria-label="Close popup"
            >
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Popup image */}
            <img
              src={`${cloudfrontUrl}/${import.meta.env.VITE_POPUP_IMAGE_URL}`}
              alt="Popup"
              className="max-w-full max-h-full w-auto h-auto rounded-lg shadow-2xl object-contain"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
